import { useState } from 'react';
import { Badge, Button, Card, EmptyState, ErrorState, Spinner, cx } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import {
  formatDuration,
  formatSince,
  isStale,
  jobLabel,
  statusLabel,
  statusTone,
  STALE_THRESHOLD_HOURS,
} from './batchFormat';
import type { BatchExecution } from './types';
import { useBatches } from './useAdmin';

/**
 * 배치 실행 이력. (V51)
 *
 * 왜 만들었나 (2026-08-16)
 *   키를 주입한 뒤 07:00 배치가 실제로 돌았는지 확인하려고 홈서버에 SSH 로 붙어
 *   docker logs 를 뒤지고 DB 를 직접 조회했다. 매번 이럴 수는 없다.
 *
 * 화면이 답해야 하는 질문 셋
 *   1. 지금 다 정상인가            → 맨 위 잡별 카드
 *   2. 어제·오늘 무슨 일이 있었나   → 아래 이력 목록
 *   3. 이 잡이 왜 실패했나          → 실패 행의 메시지
 *
 * 🔴 다시 돌리는 버튼이 없다
 *   자동 종목 선별을 손으로 재실행할 수 있게 되면 결과가 마음에 안 들 때
 *   기준을 낮춰 다시 돌리고 싶어진다. (CLAUDE.md 1.3절)
 */
export function BatchPanel() {
  const [job, setJob] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const query = useBatches(job, page);

  if (query.isLoading) {
    return <Spinner label="배치 이력을 불러오는 중" />;
  }

  if (query.isError) {
    return (
      <ErrorState
        message={query.error?.message ?? '배치 이력을 불러오지 못했습니다.'}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const data = query.data;
  if (!data) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <JobStatusBoard executions={data.latestPerJob} />

      <Card
        title="실행 이력"
        action={
          <span className="text-xs text-fg-subtle">
            전체 {data.totalElements}건 · 30초마다 갱신
          </span>
        }
      >
        <JobFilter
          jobNames={data.jobNames}
          selected={job}
          onChange={(next) => {
            setJob(next);
            setPage(0);
          }}
        />

        {data.recent.length === 0 ? (
          <EmptyState
            title="이력이 없습니다"
            description="배치가 아직 한 번도 돌지 않았습니다. 다음 배치가 돌면 여기에 쌓입니다."
          />
        ) : (
          <ul className="mt-3 flex flex-col divide-y divide-line">
            {data.recent.map((execution) => (
              <li key={execution.id}>
                <ExecutionRow execution={execution} />
              </li>
            ))}
          </ul>
        )}

        {data.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button
              variant="secondary"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              이전
            </Button>
            <span className="text-xs text-fg-muted">
              {page + 1} / {data.totalPages}
            </span>
            <Button
              variant="secondary"
              disabled={page >= data.totalPages - 1}
              onClick={() => setPage((current) => current + 1)}
            >
              다음
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * 잡별 마지막 실행.
 *
 * 🔴 이 판의 핵심은 "안 돈 잡" 을 드러내는 것이다
 *   실패는 빨간 배지로 보이지만 아예 안 돈 것은 아무 표시도 없다.
 *   스케줄러가 죽으면 새 행이 안 생기고 어제 성공한 초록 배지가 그대로 남는다.
 *   2026-08-15 backend 크래시 루프가 정확히 그 상황이었다.
 */
function JobStatusBoard({ executions }: { executions: BatchExecution[] }) {
  if (executions.length === 0) {
    return (
      <Card title="지금 상태">
        <EmptyState
          title="아직 실행된 배치가 없습니다"
          description="배포 직후에는 정상입니다. 다음 배치가 돌면 잡별 상태가 여기에 뜹니다."
        />
      </Card>
    );
  }

  const staleCount = executions.filter((execution) => isStale(execution)).length;
  const failedCount = executions.filter((e) => e.status === 'FAILED').length;

  return (
    <Card
      title="지금 상태"
      action={
        failedCount + staleCount === 0 ? (
          <Badge tone="success">정상</Badge>
        ) : (
          <Badge tone="danger">
            {failedCount > 0 && `실패 ${failedCount}`}
            {failedCount > 0 && staleCount > 0 && ' · '}
            {staleCount > 0 && `지각 ${staleCount}`}
          </Badge>
        )
      }
    >
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {executions.map((execution) => (
          <li
            key={execution.jobName}
            className={cx(
              'flex items-center justify-between gap-2 rounded-lg border px-3 py-2',
              execution.status === 'FAILED' || isStale(execution)
                ? 'border-danger'
                : 'border-line',
            )}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-fg">
                {jobLabel(execution.jobName)}
              </p>
              <p className="text-xs text-fg-subtle">
                {formatSince(execution.startedAt)}
                {execution.affectedCount !== null && ` · ${execution.affectedCount}건`}
              </p>
            </div>

            <div className="shrink-0 text-right">
              <Badge tone={statusTone(execution.status)}>
                {statusLabel(execution.status)}
              </Badge>
              {isStale(execution) && (
                <p className="mt-0.5 text-xs text-danger">
                  {STALE_THRESHOLD_HOURS}시간 이상 실행 없음
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function JobFilter({
  jobNames,
  selected,
  onChange,
}: {
  jobNames: string[];
  selected: string | null;
  onChange: (job: string | null) => void;
}) {
  if (jobNames.length === 0) {
    return null;
  }

  return (
    <div className="-mx-4 overflow-x-auto px-4">
      <div className="flex gap-1 pb-1">
        <FilterChip active={selected === null} onClick={() => onChange(null)}>
          전체
        </FilterChip>
        {jobNames.map((name) => (
          <FilterChip
            key={name}
            active={selected === name}
            onClick={() => onChange(name)}
          >
            {jobLabel(name)}
          </FilterChip>
        ))}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'min-h-[32px] whitespace-nowrap rounded-full px-3 text-xs',
        active ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
      )}
    >
      {children}
    </button>
  );
}

function ExecutionRow({ execution }: { execution: BatchExecution }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-fg">{jobLabel(execution.jobName)}</span>
          <Badge tone={statusTone(execution.status)}>{statusLabel(execution.status)}</Badge>
        </div>

        <p className="text-xs text-fg-subtle">
          {formatDateTime(execution.startedAt)} · {formatDuration(execution.durationMs)}
          {execution.affectedCount !== null && ` · ${execution.affectedCount}건`}
        </p>

        {execution.detail && (
          <p className="mt-0.5 text-xs text-fg-muted">{execution.detail}</p>
        )}

        {/*
          실패 메시지는 마스킹·절삭된 값이다. 스택트레이스가 아니다.
          DART 인증키가 URL 에 실려 오므로 서버가 지우고 저장한다.
          전체 원인이 필요하면 컨테이너 로그를 본다.
        */}
        {execution.errorMessage && (
          <p className="mt-1 break-words rounded bg-surface-hover px-2 py-1 text-xs text-danger">
            {execution.errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}
